import { useMemo, useRef, useState } from 'react';
import { api, ApiError, type AccountRow, type Portal } from '@/api/client';
import { fmtDate, fmtNum } from '@/lib/format';
import { PORTAL_COPY } from '@/lib/portal';
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  SectionHeader,
  StatusPill,
  cell,
  cx,
  headCell,
  input,
  mutedText,
  numCell,
  row,
} from '@/ui';

type SortKey = 'name' | 'createdAt' | 'listings' | 'live';

function nameOf(a: AccountRow): string {
  return a.fullName?.trim() || a.email;
}

/** Locks are "connected" once at least one lock is live on the account. */
function locksOn(a: AccountRow): boolean {
  return (a.numLiveLocks ?? 0) > 0;
}

function upsellTypes(a: AccountRow): string[] {
  return [a.upsells.eciLco && 'Early check-in / late checkout', a.upsells.bookingBooster && 'Gap nights'].filter(
    (t): t is string => !!t
  );
}

/** Voice routing is the switch that puts the phone system in front of guests. */
function phoneOn(a: AccountRow): boolean {
  return a.voiceRouting;
}

export function AccountsTable({ rows, portal }: { rows: AccountRow[]; portal: Portal }) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [copiedFor, setCopiedFor] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => nameOf(r).toLowerCase().includes(q) || r.email.toLowerCase().includes(q))
      : rows.slice();
    filtered.sort((a, b) => {
      if (sort === 'createdAt') return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
      if (sort === 'listings') return (b.numListingsEnabled ?? 0) - (a.numListingsEnabled ?? 0);
      if (sort === 'live') return Number(b.bffLive) - Number(a.bffLive) || nameOf(a).localeCompare(nameOf(b));
      return nameOf(a).localeCompare(nameOf(b));
    });
    return filtered;
  }, [rows, query, sort]);

  async function copyOneTimeLink(userId: number) {
    const requestId = ++requestIdRef.current;
    setBusy(userId);
    setError(null);
    try {
      const { url } = await api.oneTimeLink(portal.key, userId);
      if (requestId !== requestIdRef.current) return;
      await navigator.clipboard.writeText(url);
      if (requestId !== requestIdRef.current) return;
      setCopiedFor(userId);
      setTimeout(() => {
        setCopiedFor((c) => (c === userId ? null : c));
      }, 2500);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      // The link is a live credential into a customer account, so failing quietly is
      // worse than saying nothing worked.
      setError(
        err instanceof ApiError && err.status === 403
          ? 'That location is not available to your account.'
          : 'Could not generate a one time link. Try again.'
      );
    } finally {
      if (requestId === requestIdRef.current) setBusy(null);
    }
  }

  return (
    <Card>
      <SectionHeader
        title={portal.key === 'skyrun' ? 'Franchise locations' : 'Locations'}
        subtitle={`${fmtNum(rows.length)} ${PORTAL_COPY[portal.key].accounts} accounts, with feature enablement and go-live status`}
        right={
          <div className="flex items-center gap-2">
            <input
              className={cx(input, 'w-56')}
              placeholder="Search name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select className={cx(input, 'pr-8')} value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
              <option value="name">Sort: name</option>
              <option value="createdAt">Sort: newest</option>
              <option value="listings">Sort: listings</option>
              <option value="live">Sort: live first</option>
            </select>
          </div>
        }
      />

      {error && (
        <div className="px-4 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}

      <div className="max-h-[560px] overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={headCell}>Location</th>
              <th className={cx(headCell, 'text-right')}>Created</th>
              <th className={cx(headCell, 'text-right')}>Listings</th>
              <th className={cx(headCell, 'text-right')}>Listings live</th>
              <th className={headCell}>Inbox</th>
              <th className={headCell}>Upsells enabled</th>
              <th className={headCell}>Locks</th>
              <th className={headCell}>Cleaning &amp; ops</th>
              <th className={headCell}>Phone system</th>
              <th className={cx(headCell, 'text-right')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((a) => (
              <tr key={a.userId} className={row}>
                <td className={cell}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{nameOf(a)}</span>
                    {a.isDemo && <Badge tone="neutral">Demo</Badge>}
                  </div>
                  <div className="max-w-[240px] truncate text-xs text-neutral-500" title={a.email}>
                    #{a.userId} · {a.email}
                  </div>
                </td>
                <td className={numCell}>{fmtDate(a.createdAt)}</td>
                <td className={numCell}>{fmtNum(a.numListings)}</td>
                <td className={numCell}>{fmtNum(a.numListingsEnabled)}</td>
                <td className={cell}>
                  {a.bffLive ? <Badge tone="green">Live</Badge> : <Badge tone="neutral">Onboarding</Badge>}
                </td>
                <td className={cell}>
                  <StatusPill
                    on={upsellTypes(a).length > 0}
                    label={upsellTypes(a).length > 0 ? `${upsellTypes(a).length} enabled` : 'Not enabled'}
                    title={upsellTypes(a).join(', ') || undefined}
                  />
                </td>
                <td className={cell}>
                  <StatusPill
                    on={locksOn(a)}
                    label={locksOn(a) ? `${fmtNum(a.numLiveLocks)} connected` : 'Not connected'}
                  />
                </td>
                <td className={cell}>
                  <StatusPill on={a.opsLive} label={a.opsLive ? 'Enabled' : 'Not enabled'} />
                </td>
                <td className={cell}>
                  <StatusPill
                    on={phoneOn(a)}
                    label={phoneOn(a) ? 'Enabled' : 'Not enabled'}
                    title={a.phoneNumberValue ?? undefined}
                  />
                </td>
                <td className={cx(cell, 'text-right')}>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button variant="ghost" disabled={busy === a.userId} onClick={() => void copyOneTimeLink(a.userId)}>
                      {copiedFor === a.userId ? 'Copied' : 'Copy login link'}
                    </Button>
                    <a
                      href={api.magicLoginUrl(portal.key, a.userId)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-neutral-200 bg-white px-3 text-[13px] font-medium text-neutral-900 transition-colors hover:bg-neutral-50"
                    >
                      Log in
                    </a>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td className={cx(cell, mutedText)} colSpan={10}>
                  No locations match that search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
